import { DEFAULT_CALL_API } from "../../../../../helpers/constant";
import type { StepTransferBranch } from "@gpms/survey-form";

/** Id preview chỉ dùng trong payload Redux/canvas — không ghi vào form state. */
const PREVIEW_GROUP_CONDITION_ID_BASE = -1000;

const DEFAULT_MAX_TRANSFER_STEPS = 10; // không set env => dùng trần mặc định này
const rawMaxTransferSteps = Number(process.env.REACT_APP_MAX_TRANSFER_STEPS);
/** Trần số dòng chuyển bước (số nguyên > 0). Không set / không hợp lệ =>
 *  DEFAULT_MAX_TRANSFER_STEPS. Logic xử lý luôn là bản tổng quát theo N;
 *  hằng này chỉ là tham số trần. */
export const MAX_TRANSFER_STEPS: number =
  Number.isFinite(rawMaxTransferSteps) && rawMaxTransferSteps > 0
    ? Math.floor(rawMaxTransferSteps)
    : DEFAULT_MAX_TRANSFER_STEPS;

export const canAddTransferStep = (len: number) => len < MAX_TRANSFER_STEPS;

/** Sắp xếp theo transfer_order (identity nhánh) — không mutate mảng gốc. */
export function sortStepTranferByTransferOrder<
  T extends { transfer_order?: string | number | null }
>(stepTranfer: T[]): T[] {
  return [...stepTranfer]
    .map((row, originalIdx) => ({ row, originalIdx }))
    .sort((a, b) => {
      const ao = Number(a.row.transfer_order ?? a.originalIdx);
      const bo = Number(b.row.transfer_order ?? b.originalIdx);
      return ao - bo || a.originalIdx - b.originalIdx;
    })
    .map(({ row }) => row);
}

/** Gán transfer_order = chỉ số nhánh (0, 1, 2…) sau thêm/xóa OR. */
export function renumberStepTranferTransferOrders<
  T extends { transfer_order?: string | number | null }
>(stepTranfer: T[]): T[] {
  return stepTranfer.map((row, idx) => ({
    ...row,
    transfer_order: String(idx)
  }));
}

/** Tái lập invariant sau thêm/xóa/cắt nhánh trên list dạng merged/UI:
 *  sort theo transfer_order, đánh lại transfer_order liên tục 0..N-1,
 *  dòng cuối luôn có step_condition = null. */
export function normalizeStepTranfer<
  T extends {
    transfer_order?: string | number | null;
    step_condition?: number | string | null;
  }
>(stepTranfer: T[]): T[] {
  const renumbered = renumberStepTranferTransferOrders(
    sortStepTranferByTransferOrder(stepTranfer)
  );
  if (renumbered.length) {
    renumbered[renumbered.length - 1] = {
      ...renumbered[renumbered.length - 1],
      step_condition: null
    };
  }
  return renumbered;
}

const toNumberOrNull = (value: any): number | null => {
  if (value == null || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const toNextStepType = (value: any): StepTransferBranch["next_step_type"] => {
  const num = Number(value);
  return num === 1 || num === 2 || num === 3 ? (num as 1 | 2 | 3) : null;
};

const toLogicalOperator = (
  value: any
): StepTransferBranch["step_condition"] => {
  const num = Number(value);
  return num === 1 || num === 2 ? (num as 1 | 2) : null;
};

/**
 * Chuẩn hoá dữ liệu thô `stepTranfer` (gpms-app) thành đầu vào tiêu chuẩn
 * `StepTransferBranch[]` cho các hook của thư viện survey-form — NƠI DUY NHẤT
 * ép kiểu/normalize ở biên gpms-app ↔ thư viện. Sort theo `transfer_order` rồi
 * đánh số lại liên tục 0..N-1 (số), map các trường sang kiểu chuẩn (null khi
 * thiếu). Không mutate mảng gốc. Hook KHÔNG tự sort/suy diễn — chỉ đọc các
 * trường chuẩn trên nhánh này.
 */
export function normalizeStepTransferInput(
  stepTranfer: any[] | null | undefined
): StepTransferBranch[] {
  if (!Array.isArray(stepTranfer) || !stepTranfer.length) return [];
  return sortStepTranferByTransferOrder(stepTranfer).map((row, idx) => ({
    transfer_order: idx,
    step_order: toNumberOrNull(row?.step_order),
    group_step_order: toNumberOrNull(row?.group_step_order),
    next_step_type: toNextStepType(row?.next_step_type),
    step_condition: toLogicalOperator(row?.step_condition),
    group_step_condition: toLogicalOperator(row?.group_step_condition)
  }));
}

/**
 * Prune các dòng chuyển không hợp lệ của TỪNG bước sau khi thêm/xóa bước của
 * quy trình (hợp nhất 4 bản copy-paste `deleteWrongStepTransfer`; lấy semantics
 * bản my-process — bản duy nhất so sánh đúng `.step_order`):
 * - dòng tự trỏ về chính bước nguồn;
 * - dòng trỏ tới bước vượt quá bước cuối của quy trình.
 * Mutate `stepTransferCondition` tại chỗ (giữ hành vi các bản gốc) rồi
 * normalize (renumber + dòng cuối null). Trả về danh sách row đã bị cắt — đã
 * bung thêm entry `{...row, id: group_step_order_id}` cho cặp row BE — để
 * caller gửi API xóa nếu màn hình đó persist ngay (my-process); các màn thuần
 * FE state (create/copy/version) bỏ qua kết quả này.
 */
export function pruneWrongStepTransfer(listSortedStep: any[]): any[] {
  const removedRows: any[] = [];
  if (!Array.isArray(listSortedStep) || !listSortedStep.length) {
    return removedRows;
  }
  const lastStep = listSortedStep[listSortedStep.length - 1];
  const maxStepOrder = Number(lastStep?.step_order ?? listSortedStep.length);
  listSortedStep.forEach((step: any) => {
    if (!step?.stepTransferCondition?.length) return;
    const kept: any[] = [];
    step.stepTransferCondition.forEach((row: any) => {
      // step_order null = chưa chọn đích (vd. đích vừa bị xóa — xem
      // shiftStepTransferTargetsOnRemove), không phải tự trỏ: null + 1 = 1 sẽ
      // khớp nhầm bước đầu tiên.
      const pointsToSelf =
        (row.step_order != null && step.step_order === row.step_order + 1) ||
        (row.group_step_order && step.step_order === row.group_step_order + 1);
      // null/undefined an toàn: null + 1 = 1, undefined + 1 = NaN — đều không > max.
      const pointsOutOfRange =
        row.step_order + 1 > maxStepOrder ||
        row.group_step_order + 1 > maxStepOrder;
      if (pointsToSelf || pointsOutOfRange) {
        if (row.id) {
          removedRows.push(row);
        }
        if (row.group_step_order_id) {
          removedRows.push({ ...row, id: row.group_step_order_id });
        }
      } else {
        kept.push(row);
      }
    });
    step.stepTransferCondition = normalizeStepTranfer(kept);
  });
  return removedRows;
}

/**
 * Dời đích chuyển bước của MỌI bước theo ánh xạ vị trí cũ → mới sau khi
 * thêm/xóa bước ở các màn thuần FE state (create/copy/version). Đích lưu theo
 * chỉ số 0-based của bước đích (row.step_order / row.group_step_order), không
 * theo id — màn chi tiết đọc lại từ BE nên không cần, còn các màn này phải tự
 * dời; nếu không, pruneWrongStepTransfer coi dòng của các bước phía sau là "tự
 * trỏ" và cắt mất. `mapIndex` trả null = bước đích đã bị xóa → để trống đích
 * (giữ nguyên nhánh) để validate bắt người dùng chọn lại.
 * Gán mảng/row mới (row có thể dùng chung tham chiếu) nhưng gán lại trên chính
 * object bước — caller truyền danh sách đã clone.
 */
function remapStepTransferTargets(
  steps: any[],
  mapIndex: (index: number) => number | null
): void {
  (steps ?? []).forEach((step: any) => {
    if (!Array.isArray(step?.stepTransferCondition)) return;
    step.stepTransferCondition = step.stepTransferCondition.map((row: any) => {
      if (row == null) return row;
      const next = { ...row };
      (["step_order", "group_step_order"] as const).forEach((field) => {
        const index = toNumberOrNull(row[field]);
        if (index != null) next[field] = mapIndex(index);
      });
      return next;
    });
  });
}

/** Chèn `count` bước mới tại vị trí `insertAt` (0-based): đích >= insertAt dời
 *  +count — mỗi dòng vẫn trỏ đúng bước cũ, bước mới để trống. */
export function shiftStepTransferTargetsOnInsert(
  steps: any[],
  insertAt: number,
  count = 1
): void {
  remapStepTransferTargets(steps, (index) =>
    index >= insertAt ? index + count : index
  );
}

/** Xóa `count` bước liên tiếp từ vị trí `removeFrom` (0-based): đích trỏ vào
 *  bước bị xóa → null, đích phía sau dời −count. */
export function shiftStepTransferTargetsOnRemove(
  steps: any[],
  removeFrom: number,
  count = 1
): void {
  remapStepTransferTargets(steps, (index) => {
    if (index < removeFrom) return index;
    if (index < removeFrom + count) return null;
    return index - count;
  });
}

/** Chữ ký topology dòng chuyển của một bước — bỏ qua id/group id và thứ tự
 *  mảng, chỉ giữ các trường quyết định đích và toán tử. */
const stepTransferSignature = (rows: any[] | undefined): string =>
  JSON.stringify(
    sortStepTranferByTransferOrder(
      Array.isArray(rows) ? rows.filter(Boolean) : []
    ).map((row: any) => [
      toNumberOrNull(row.next_step_type),
      toNumberOrNull(row.step_order),
      toNumberOrNull(row.group_step_order),
      toNumberOrNull(row.step_condition),
      toNumberOrNull(row.group_step_condition)
    ])
  );

/**
 * Các bước (đã có id) có dòng chuyển khác đi giữa `prevSteps` và `nextSteps`
 * sau khi thêm/xóa/đổi tổng số bước ở màn persist ngay (chi tiết quy trình) —
 * để chỉ ghi lại xuống BE đúng các bước bị đổi:
 * - `replaced`: bước còn dòng → ghi lại toàn bộ dòng của bước;
 * - `emptiedRows`: dòng cũ (bung cả cặp row cụm) của bước hết dòng → xóa theo
 *   id, vì replace với mảng rỗng không xóa gì.
 * Bước không có trong `prevSteps` (bước vừa tạo) bỏ qua.
 */
export function collectChangedStepTransfers(
  prevSteps: any[],
  nextSteps: any[]
): { replaced: Array<{ stepId: any; rows: any[] }>; emptiedRows: any[] } {
  const prevRowsById = new Map<any, any[]>();
  (prevSteps ?? []).forEach((step: any) => {
    if (step?.id) prevRowsById.set(step.id, step.stepTransferCondition ?? []);
  });
  const replaced: Array<{ stepId: any; rows: any[] }> = [];
  const emptiedRows: any[] = [];
  (nextSteps ?? []).forEach((step: any) => {
    if (!step?.id || !prevRowsById.has(step.id)) return;
    const prevRows = prevRowsById.get(step.id) ?? [];
    const nextRows = Array.isArray(step.stepTransferCondition)
      ? step.stepTransferCondition
      : [];
    if (stepTransferSignature(prevRows) === stepTransferSignature(nextRows)) {
      return;
    }
    if (nextRows.length) {
      replaced.push({ stepId: step.id, rows: nextRows });
      return;
    }
    prevRows.forEach((row: any) => {
      if (row?.id) emptiedRows.push(row);
      if (row?.group_step_order_id) {
        emptiedRows.push({ ...row, id: row.group_step_order_id });
      }
    });
  });
  return { replaced, emptiedRows };
}

/** Toán tử nối bước (step_condition / group_step_condition). */
export const STEP_OPERATOR_AND = 1;
export const STEP_OPERATOR_OR = 2;

const isStepOperator = (value: any): boolean =>
  value === STEP_OPERATOR_AND || value === STEP_OPERATOR_OR;

export function oppositeStepOperator(operator: number): number {
  return operator === STEP_OPERATOR_AND ? STEP_OPERATOR_OR : STEP_OPERATOR_AND;
}

/**
 * Toán tử level 1 (nối các dòng chuyển bước) của một list transfer — chạy đúng
 * cho cả 2 shape:
 * - Row merged/UI (có group_step_condition/group_step_order — 2 field này chỉ
 *   sinh ra khi merge hoặc khi thao tác trên form, flat row từ BE không có):
 *   1 row = cả cụm, step_condition chính là level 1. Riêng row merged dở dang
 *   (cặp flat thiếu row 2) có step_condition === group_step_condition (giá trị
 *   tạm level 2) — không tin làm level 1.
 * - Flat row (BE): cụm nhóm = 2 row chung group_condition_id — row thứ NHẤT
 *   mang toán tử TRONG nhóm (level 2) trong step_condition, row thứ HAI mới
 *   mang level 1 (convention của returnStepTranfer / mergeStepTranferGroup /
 *   mappingNextStep).
 * Không row nào mang level 1 có giá trị (vd list chỉ có 1 cụm) => level 1 =
 * toán tử NGƯỢC của toán tử trong nhóm đầu tiên đọc được. Null khi không xác
 * định được.
 */
export function getSharedStepCondition(stepTranfer: any[]): number | null {
  if (!Array.isArray(stepTranfer)) return null;
  const seenFlatGroupIds = new Set<any>();
  let firstGroupLevel2: number | null = null;
  for (const row of stepTranfer) {
    if (row == null) continue;
    const isMergedGroupRow =
      row.group_step_condition != null || row.group_step_order != null;
    if (isMergedGroupRow) {
      if (
        firstGroupLevel2 == null &&
        isStepOperator(row.group_step_condition)
      ) {
        firstGroupLevel2 = row.group_step_condition;
      }
      if (
        isStepOperator(row.step_condition) &&
        row.step_condition !== row.group_step_condition
      ) {
        return row.step_condition;
      }
      continue;
    }
    if (row.group_condition_id != null) {
      if (!seenFlatGroupIds.has(row.group_condition_id)) {
        // Row thứ nhất của cụm flat: step_condition là toán tử TRONG nhóm.
        seenFlatGroupIds.add(row.group_condition_id);
        if (firstGroupLevel2 == null && isStepOperator(row.step_condition)) {
          firstGroupLevel2 = row.step_condition;
        }
        continue;
      }
    }
    if (isStepOperator(row.step_condition)) {
      return row.step_condition;
    }
  }
  return firstGroupLevel2 == null
    ? null
    : oppositeStepOperator(firstGroupLevel2);
}

/** Toán tử TRONG nhóm (level 2) — theo invariant mọi cụm dùng CHUNG một toán
 *  tử nên đọc từ cụm đầu tiên tìm thấy. Null nếu list không có cụm. */
export function getGroupStepCondition(stepTranfer: any[]): number | null {
  if (!Array.isArray(stepTranfer)) return null;
  const seenFlatGroupIds = new Set<any>();
  for (const row of stepTranfer) {
    if (row == null) continue;
    if (isStepOperator(row.group_step_condition)) {
      return row.group_step_condition;
    }
    const isMergedGroupRow =
      row.group_step_condition != null || row.group_step_order != null;
    if (!isMergedGroupRow && row.group_condition_id != null) {
      if (!seenFlatGroupIds.has(row.group_condition_id)) {
        seenFlatGroupIds.add(row.group_condition_id);
        if (isStepOperator(row.step_condition)) {
          return row.step_condition;
        }
      }
    }
  }
  return null;
}

/** NGUYÊN TẮC 2 LEVEL TOÁN TỬ: toán tử trong cụm (level 2) luôn là toán tử
 *  NGƯỢC của level 1. Chưa xác định được level 1 => mặc định VÀ (giữ hành vi
 *  cũ của changeRemoveGroupId). */
export function deriveGroupStepCondition(stepTranfer: any[]): number {
  const shared = getSharedStepCondition(stepTranfer);
  return shared == null ? STEP_OPERATOR_AND : oppositeStepOperator(shared);
}

/** Form đủ field để validate UI và cho phép sync canvas. */
export function isStepTranferBranchTopologyComplete(branch: any): boolean {
  if (branch.next_step_type == null) return false;
  if (branch.next_step_type === DEFAULT_CALL_API.TYPE) return true;
  if (branch.next_step_type === DEFAULT_CALL_API.UNIT) {
    return (
      branch.group_step_order != null && branch.group_step_condition != null
    );
  }
  return branch.step_order != null;
}

export function isStepTranferTopologyCompleteForFlow(
  stepTranfer: any[] | undefined
): boolean {
  if (!stepTranfer?.length) return false;
  return stepTranfer.every(isStepTranferBranchTopologyComplete);
}

/**
 * Bản sao topology cho canvas: sort theo transfer_order + preview group_condition_id.
 * Không renumber — transfer_order là identity nhánh (chỉ renumber khi thêm/xóa OR ở Tranfer).
 */
/** Đọc entry survey theo tranfer_order (identity nhánh), không theo index mảng / UI. */
export function findTransitionConditionByTransferOrder<
  T extends { tranfer_order?: string | number | null }
>(
  transitionConditions: Array<T | null> | null | undefined,
  transferOrder: string | number | null | undefined
): T | null {
  if (!Array.isArray(transitionConditions)) return null;
  const raw =
    transferOrder != null && String(transferOrder).trim() !== ""
      ? Number(transferOrder)
      : NaN;
  if (Number.isNaN(raw)) return null;
  return (
    transitionConditions.find(
      (entry) => entry != null && Number(entry.tranfer_order) === raw
    ) ?? null
  );
}

/**
 * R1 — chống vòng lặp chuyển bước: tập chỉ số (0-based, trùng semantics
 * option.id của listOptionStepProcess) các bước có đường đi chuyển bước TỚI
 * bước `currentIndex` ("tổ tiên" trong đồ thị chuyển bước). Chọn đích ngoài
 * tập này không thể tạo chu trình — thay cặp đệ quy
 * deleteOptionDuplicate/deleteOptionInGroupStep cũ (vốn loại nhầm cả các đích
 * anh em của bước cha). BFS ngược, mỗi bước thăm đúng một lần.
 * Cạnh: i → row.step_order và i → row.group_step_order với row thuộc
 * stepTransferCondition của bước i; target không phải số thì bỏ qua.
 */
export function collectAncestorStepIndexes(
  steps: any[],
  currentIndex: number
): Set<number> {
  const predecessorsByTarget = new Map<number, number[]>();
  (steps ?? []).forEach((step: any, sourceIndex: number) => {
    step?.stepTransferCondition?.forEach((row: any) => {
      [row?.step_order, row?.group_step_order].forEach((target: any) => {
        if (typeof target !== "number") return;
        const sources = predecessorsByTarget.get(target) ?? [];
        sources.push(sourceIndex);
        predecessorsByTarget.set(target, sources);
      });
    });
  });
  const ancestors = new Set<number>();
  const queue: number[] = [currentIndex];
  while (queue.length) {
    const node = queue.shift() as number;
    (predecessorsByTarget.get(node) ?? []).forEach((source) => {
      if (source !== currentIndex && !ancestors.has(source)) {
        ancestors.add(source);
        queue.push(source);
      }
    });
  }
  return ancestors;
}

export type TransferTargetField = "step" | "group";
/** Level điều kiện: 1 = dòng thường, 2 = thành viên cụm (nhóm bước). */
export type TransferTargetLevel = 1 | 2;

/**
 * Occurrence các đích đã chọn trên list transfer đang chỉnh, theo thứ tự dòng
 * (trong 1 dòng: vế step trước vế group), kèm level điều kiện:
 * - dòng thường (type 1): step_order thuộc level 1;
 * - dòng cụm (type 2): CẢ step_order lẫn group_step_order đều là thành viên
 *   cụm → level 2;
 * - dòng END (type 3): không tính step_order — giá trị có thể stale sau khi
 *   đổi loại vì changeRemoveGroupId không reset nó.
 */
export function collectSelectedTransferTargets(stepTranfer: any[]): Array<{
  value: number;
  rowIndex: number;
  field: TransferTargetField;
  level: TransferTargetLevel;
}> {
  const occurrences: Array<{
    value: number;
    rowIndex: number;
    field: TransferTargetField;
    level: TransferTargetLevel;
  }> = [];
  (stepTranfer ?? []).forEach((row: any, rowIndex: number) => {
    if (row == null) return;
    const isGroupRow = row.next_step_type === DEFAULT_CALL_API.UNIT;
    if (
      row.next_step_type !== DEFAULT_CALL_API.TYPE &&
      typeof row.step_order === "number"
    ) {
      occurrences.push({
        value: row.step_order,
        rowIndex,
        field: "step",
        level: isGroupRow ? 2 : 1
      });
    }
    if (isGroupRow && typeof row.group_step_order === "number") {
      occurrences.push({
        value: row.group_step_order,
        rowIndex,
        field: "group",
        level: 2
      });
    }
  });
  return occurrences;
}

/** Có dòng nào trỏ tới bước kết thúc không — tính cả dòng thường lẫn cụm,
 *  cả vế step lẫn vế group. Dòng END (type 3) không tính (đã loại ở
 *  collectSelectedTransferTargets). */
export function hasEndStepTransferTarget(
  stepTranfer: any[],
  stepsLength: number
): boolean {
  const lastIndex = (stepsLength ?? 0) - 1;
  if (lastIndex < 1) return false;
  return collectSelectedTransferTargets(stepTranfer).some(
    (occurrence) => occurrence.value === lastIndex
  );
}

/**
 * Rule: đã có đích là bước kết thúc => toán tử level 1 bắt buộc HOẶC ("VÀ
 * kết thúc" vô nghĩa — collectAllowedTransferTargetIds cũng loại bước kết
 * thúc khi level 1 = VÀ). Ép mọi dòng đang mang toán tử level 1 sang HOẶC;
 * theo nguyên tắc 2 level, cụm chuyển sang toán tử NGƯỢC (VÀ) — nhưng chỉ khi
 * level 1 thực sự tồn tại (list 1 dòng: toán tử cụm do người dùng tự chọn,
 * giữ nguyên). Không có đích kết thúc / không có level 1 => trả về list gốc.
 */
export function enforceOrOperatorForEndStepTarget(
  stepTranfer: any[],
  stepsLength: number
): any[] {
  if (
    !Array.isArray(stepTranfer) ||
    !hasEndStepTransferTarget(stepTranfer, stepsLength)
  ) {
    return stepTranfer;
  }
  const hasLevel1 = stepTranfer.some((row: any) =>
    isStepOperator(row?.step_condition)
  );
  if (!hasLevel1) return stepTranfer;
  const groupStepCondition = oppositeStepOperator(STEP_OPERATOR_OR);
  return stepTranfer.map((row: any) => {
    if (row == null) return row;
    const next = { ...row };
    if (isStepOperator(row.step_condition)) {
      next.step_condition = STEP_OPERATOR_OR;
    }
    if (row.next_step_type === DEFAULT_CALL_API.UNIT) {
      next.group_step_condition = groupStepCondition;
    }
    return next;
  });
}

/**
 * R2 — chống trùng đích trong config một bước, xét TRONG TỪNG LEVEL (bước nằm
 * trong cụm không tính trùng với bước ở dòng thường vì thuộc ngữ cảnh điều
 * kiện khác):
 * - Level 1 (dòng thường): trùng theo GIÁ TRỊ giữa các dòng thường —
 *   occurrence đầu tiên thắng, dòng sau bị loại khỏi options → flag invalid.
 * - Level 2 (cụm): từng thành viên ĐƯỢC PHÉP xuất hiện lại ở cụm khác dòng;
 *   chỉ chặn (a) hai vế trong cùng cụm trùng nhau — vế step đứng trước nên giữ
 *   giá trị, vế group bị loại → flag; (b) cả CẶP thành viên trùng nguyên với
 *   cụm ở dòng khác, so theo tập không phân biệt vế nào đứng trước. Cặp trùng
 *   với dòng TRƯỚC → giá trị đang chọn bị loại → flag invalid (kể cả dữ liệu
 *   cũ); cặp trùng với dòng SAU chỉ chặn lựa chọn mới, không flag giá trị
 *   đang có.
 */
export function filterDuplicateTransferTargets(
  options: Array<{ id: number; name: string }>,
  stepTranfer: any[],
  rowIndex: number,
  field: TransferTargetField
): Array<{ id: number; name: string }> {
  const rows = stepTranfer ?? [];
  const callerRow = rows[rowIndex];
  const isGroupCaller = callerRow?.next_step_type === DEFAULT_CALL_API.UNIT;

  if (!isGroupCaller) {
    const firstOccurrenceByValue = new Map<
      number,
      { rowIndex: number; field: TransferTargetField }
    >();
    collectSelectedTransferTargets(rows).forEach((occurrence) => {
      if (
        occurrence.level === 1 &&
        !firstOccurrenceByValue.has(occurrence.value)
      ) {
        firstOccurrenceByValue.set(occurrence.value, occurrence);
      }
    });
    return options.filter((option) => {
      const first = firstOccurrenceByValue.get(option.id);
      if (!first) return true;
      return first.rowIndex === rowIndex && first.field === field;
    });
  }

  const ownValue =
    field === "step" ? callerRow.step_order : callerRow.group_step_order;
  const otherValue =
    field === "step" ? callerRow.group_step_order : callerRow.step_order;

  return options.filter(({ id: candidate }) => {
    // (a) Hai vế trong cùng cụm không được trùng nhau. Vế step giữ giá trị
    // hiện tại của mình (candidate === step_order) để dữ liệu cũ dạng (x, x)
    // chỉ flag ở vế group — nhất quán "vế đứng trước thắng".
    if (field === "group") {
      if (
        typeof callerRow.step_order === "number" &&
        candidate === callerRow.step_order
      ) {
        return false;
      }
    } else if (
      typeof callerRow.group_step_order === "number" &&
      candidate === callerRow.group_step_order &&
      candidate !== callerRow.step_order
    ) {
      return false;
    }
    // (b) Cặp thành viên không được trùng nguyên với cụm ở dòng khác.
    if (typeof otherValue !== "number") return true;
    for (let j = 0; j < rows.length; j++) {
      const row = rows[j];
      if (j === rowIndex || row?.next_step_type !== DEFAULT_CALL_API.UNIT) {
        continue;
      }
      if (
        typeof row.step_order !== "number" ||
        typeof row.group_step_order !== "number"
      ) {
        continue;
      }
      const samePair =
        (candidate === row.step_order && otherValue === row.group_step_order) ||
        (candidate === row.group_step_order && otherValue === row.step_order);
      if (samePair && !(candidate === ownValue && j > rowIndex)) {
        return false;
      }
    }
    return true;
  });
}

/**
 * Tập id (0-based, semantics option.id) các đích ĐƯỢC PHÉP chọn cho một
 * combobox chuyển bước — NGUỒN CHUNG cho option list trên UI (Tranfer) và
 * validate lúc save (validateStepFormData) để hai bên không lệch nhau:
 * - quy tắc nền: bỏ bước 1, slot null, chính bước hiện tại; bước kết thúc chỉ
 *   hiện khi không phải bước đầu;
 * - toán tử VÀ (level 1 đọc ở dòng đầu, hoặc level 2 của chính dòng) loại
 *   bước kết thúc;
 * - R1: loại tổ tiên — collectAncestorStepIndexes;
 * - R2: loại đích trùng trong bước — filterDuplicateTransferTargets.
 */
export function collectAllowedTransferTargetIds(params: {
  steps: any[];
  location: number;
  stepTranfer: any[];
  rowIndex: number;
  field: TransferTargetField;
}): Set<number> {
  const { steps, location, stepTranfer, rowIndex, field } = params;
  const lastIndex = (steps?.length ?? 0) - 1;
  let ids: number[] = [];
  (steps ?? []).forEach((step: any, i: number) => {
    if (i > 0 && step && i !== location) {
      if (i === lastIndex) {
        if (location > 0) ids.push(i);
      } else {
        ids.push(i);
      }
    }
  });
  const removesEndStep =
    stepTranfer?.[0]?.step_condition === STEP_OPERATOR_AND ||
    stepTranfer?.[rowIndex]?.group_step_condition === STEP_OPERATOR_AND;
  if (removesEndStep) {
    ids = ids.filter((id) => id !== lastIndex);
  }
  const ancestors = collectAncestorStepIndexes(steps ?? [], location);
  const options = ids
    .filter((id) => !ancestors.has(id))
    .map((id) => ({ id, name: "" }));
  return new Set(
    filterDuplicateTransferTargets(options, stepTranfer, rowIndex, field).map(
      (option) => option.id
    )
  );
}

/**
 * Bước có đích chuyển ĐÃ CHỌN nhưng vi phạm bộ lọc option (trên UI đang rơi
 * vào nhánh forceDefault + nhãn invalid) không — rule validate lúc save.
 * Chỉ soi giá trị đã chọn; giá trị còn thiếu (null) do các rule tranfer sẵn
 * có của validateStepFormData xử lý. Điều kiện soi từng vế mirror đúng
 * forceDefaultValue/forceDefaultGroupValue của Tranfer.
 */
export function hasInvalidTransferTarget(params: {
  steps: any[];
  location: number;
  stepTranfer: any[];
}): boolean {
  const { steps, location, stepTranfer } = params;
  if (!Array.isArray(stepTranfer)) return false;
  return stepTranfer.some((row: any, rowIndex: number) => {
    if (row == null) return false;
    const checks: Array<{ field: TransferTargetField; value: number }> = [];
    if (row.next_step_type !== DEFAULT_CALL_API.TYPE && row.step_order) {
      checks.push({ field: "step", value: row.step_order });
    }
    if (row.next_step_type === DEFAULT_CALL_API.UNIT && row.group_step_order) {
      checks.push({ field: "group", value: row.group_step_order });
    }
    return checks.some(
      ({ field, value }) =>
        !collectAllowedTransferTargetIds({
          steps,
          location,
          stepTranfer,
          rowIndex,
          field
        }).has(value)
    );
  });
}

export function buildStepTranferForFlowCanvas(stepTranfer: any[]): any[] {
  return sortStepTranferByTransferOrder(stepTranfer).map((row, idx) => {
    if (row.next_step_type !== DEFAULT_CALL_API.UNIT) return { ...row };
    if (row.group_condition_id != null) return { ...row };
    const order = Number(row.transfer_order ?? idx);
    return {
      ...row,
      group_condition_id: PREVIEW_GROUP_CONDITION_ID_BASE - order
    };
  });
}
